// MultiNostalgia v1 — browser entry point.
//
// This is the thin player harness described in
// docs/script-distribution.md. It does *not* know about any specific
// .munos script: on launch it HTTP-GETs the configured server's bare
// URL, compiles whatever source comes back in the browser, and opens
// a WebSocket to the same URL for the data plane. Subsequent hops do
// the same dance against the new URL.
//
// Scripts bring their own assets — inlined as base64 inside the
// `.munos` source per docs/script-distribution.md §"Single-file
// scripts". The harness ships nothing per-script.

import { MunosScriptHost, type MunosScriptHostOptions, type BatterySaveHooks } from "./munos-script-host.js";
import { loadBrowserCompiler } from "./compiler.js";
import type { MunosCompiler } from "@multinostalgia/munos";
import { LocalStorageMunosKv } from "./persistence-store.js";
import { LocalHub } from "./local-hub.js";
import type { HostSessionInfo } from "./peer-host.js";
import type { ChipId } from "@multinostalgia/core-contract";
import {
  findCore, inferCoreIdFromName, resolveCoreId, romPickerAccept, makeSynthCore,
} from "./cores/registry.js";
import { CanvasDisplay } from "./canvas-display.js";
import { overscanForSystem } from "@multinostalgia/munos";
import { AudioMixer, type AudioChannel } from "./audio-output.js";
import { InputHub } from "./input.js";
import { ControlsModal } from "./controls-modal.js";
import { RomPickerModal } from "./rom-picker-modal.js";
import { ensureProfiles } from "./bindings.js";
import {
  getRom, migrateLegacyRomCache, putRom, readRecentRoms, rememberRom,
  type RecentRom,
} from "./rom-cache.js";

import type { DestinationResolver, DeploymentResolver, HopDestination } from "./script-tenant.js";
import type { BusSocketFactory } from "./bus-connection.js";
import { WebRtcBusSocket } from "./webrtc-bus-socket.js";
import { FallbackBusSocket } from "./fallback-bus-socket.js";
import { brokerJoinSignal } from "./broker-signal.js";
import { ShareModal } from "./share-modal.js";
import { resolveServerUrl, resolveBrokerUrl, parseJoinSession, buildJoinUrl } from "./endpoints.js";
import { requestSessionInfo, type JoinerSessionInfo } from "./session-info.js";
// The persistent server store is already in the play bundle (the local
// server imports it), so this getter is a static import; only the heavier
// DB admin panel UI + CSS are lazy-loaded on first open.
import { getServerSqlDb, SERVER_OPFS_DIR } from "./persistent-server-db.js";

// The number of emulator tiles is uncapped — grow the grid as far as you
// like. Each tile is a full instance (WASM emulator + WebSocket + frame
// loop + audio channel), so a very large grid is heavy, but there's no
// hard ceiling; input realistically limits a real couch session anyway.

// Fit-to-viewport scaling. Every tile shares one integer scale, chosen so
// the whole grid's bounding box fits the viewport (scale down when a big
// grid won't fit; up to use the space when there's room). GAP values
// mirror the CSS grid gaps. The MARGINs are the real page chrome around
// the grid and track the CSS — body.player padding is 0, so only the
// .stage padding counts:
//   horizontal = .stage padding (30) per side = 60
//   vertical   = .stage padding-top (20, holds the header); padding-bottom
//                is 0 — the south-grow [+] is absolutely positioned and
//                allowed to stick out past the page, not reserve a gutter.
const GRID_COL_GAP = 8;
// Row gap holds the lower tile's header strip (~19px) so it doesn't clip
// onto the tile above; must match the CSS .tiles row-gap.
const GRID_ROW_GAP = 22;
const VIEWPORT_H_MARGIN = 64;
const VIEWPORT_V_MARGIN = 24;
const MAX_TILE_SCALE = 6;

// The south-grow [+] sits below the bottom tile and is allowed to overflow
// the viewport (the player page hides root overflow, so no scrollbar). When
// enough of it is below the fold, moving the pointer into the tile's bottom
// strip makes it "peek" up over the canvas. MARGIN/HEIGHT mirror the CSS
// (.tile-add.south margin-top + .tile-add height); peek only kicks in once
// at least PEEK_FRACTION of the button is below the viewport — so a button
// that's mostly visible stays put and is directly clickable (it won't dart
// away as the pointer approaches). PEEK_ZONE = the tile's bottom strip that
// keeps the peek active; it must be comfortably TALLER than where the
// peeked button lands (it slides up SOUTH_PEEK_SLIDE, so its top sits
// SLIDE - MARGIN above the tile bottom). With ~19px of slack above that,
// you can chase the button up and click it without leaving the zone.
const SOUTH_MARGIN = 5;
const SOUTH_HEIGHT = 22;
const SOUTH_PEEK_FRACTION = 0.75;
const SOUTH_PEEK_SLIDE = 34; // mirrors the CSS .south-peek transform
const SOUTH_PEEK_ZONE = SOUTH_PEEK_SLIDE - SOUTH_MARGIN + 19; // = 48

// Per-tile volume (0..1), persisted by player slot so a re-added tile
// recovers its level — same per-slot model as the input profiles.
const TILE_VOLUME_KEY = "tile-volume:v1";
const DEFAULT_TILE_VOLUME = 0.7;

function readTileVolumes(): Record<string, number> {
  try {
    const raw = localStorage.getItem(TILE_VOLUME_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === "object") return parsed as Record<string, number>;
    }
  } catch { /* ignore corrupt data */ }
  return {};
}

function tileVolume(slot: number): number {
  const v = readTileVolumes()[String(slot)];
  return typeof v === "number" && Number.isFinite(v)
    ? Math.max(0, Math.min(1, v))
    : DEFAULT_TILE_VOLUME;
}

function saveTileVolume(slot: number, v: number): void {
  const all = readTileVolumes();
  all[String(slot)] = Math.max(0, Math.min(1, v));
  try {
    localStorage.setItem(TILE_VOLUME_KEY, JSON.stringify(all));
  } catch { /* ignore quota errors */ }
}

// Convert a WebSocket URL (ws://host/path or wss://host/path) to the
// matching HTTP URL the server serves source from. Same host, same
// path, just the scheme swapped.
function wsToHttp(url: string): string {
  if (url.startsWith("wss://")) return "https://" + url.slice("wss://".length);
  if (url.startsWith("ws://")) return "http://" + url.slice("ws://".length);
  return url;
}

// A script fetched from the server: its source plus the server-reported
// label (its .munos basename), which scopes client-side persistence with
// the origin — see docs/persistence.md §"What identifies a script".
interface FetchedScript { source: string; label: string }

// One mod in the set a tile runs: its source, its identity label, and the
// ws URL it talks to. A tile runs an array of these as side-by-side tenants
// over one game (docs/multi-script-client.md). The default is a one-element
// array; `?scripts=` loads several. Index 0 donates the implicit
// orchestrator's identity.
interface ModSpec { source: string; label: string; url: string }

// The JSON envelope every script endpoint returns — a lone library script and
// a deployment use the SAME shape (matches serveScriptSet in the server). Each
// member's canonical label rides in the BODY, so identity is read identically
// for one script or many and survives a cross-origin fetch unchanged. (This
// replaced an X-Munos-Script-Label header, which a cross-origin browser hid
// from JS → empty labels → see docs/persistence.md §"What identifies a script".)
interface ScriptSetResponse {
  // The set's endpoint identity — a deployment id, or a lone script's own
  // label. Informational; the client keys off the `scripts` array.
  deployment?: string;
  scripts: { label: string; source: string }[];
}

// Fetch the script set the server hosts at a ws URL: a lone library script
// replies with a one-member array, a deployment with N. Every member carries
// the SAME ws URL so the host runs them over one shared bus (it keys a
// BusConnection by URL). Throws on HTTP failure.
async function fetchMods(wsUrl: string): Promise<ModSpec[]> {
  const httpUrl = wsToHttp(wsUrl);
  const r = await fetch(httpUrl, { cache: "no-cache" });
  if (!r.ok) {
    throw new Error(`HTTP ${r.status} fetching ${httpUrl}`);
  }
  const body = await r.json() as ScriptSetResponse;
  return body.scripts.map(s => ({ source: s.source, label: s.label, url: wsUrl }));
}

// Single-script convenience for the spawn path (it resolves one mod at a time):
// the endpoint serves a one-member set, so take scripts[0]. Identity comes from
// the body like everything else — no header, no URL-basename guesswork.
async function fetchScriptSource(wsUrl: string): Promise<FetchedScript> {
  const mods = await fetchMods(wsUrl);
  const first = mods[0];
  if (!first) {
    throw new Error(`empty script set fetching ${wsToHttp(wsUrl)}`);
  }
  return { source: first.source, label: first.label };
}

// The harness has no list of curated games. The user picks a ROM file;
// we hash it, cache it (rom-cache.ts: bytes in IndexedDB, recency
// index in localStorage), and expose the hash to the running script
// via `local_rom_hash()`. Whatever the server's entry script wants to
// do with that hash (a Mario lobby, a Zelda lobby, a "wrong game"
// message, drop straight into gameplay) is the script's business —
// see docs/vision.md §"Principles" #4.

// The default data-plane transport: WebRTC first, WebSocket as fallback.
//
// Every bus to a real server URL is a FallbackBusSocket that tries the WebRTC
// transport and, if it can't connect (signaling error, ICE failure, or a
// hang), transparently switches to a WebSocket — so the unreliable channel is
// used whenever the network allows it, with no loss of reach when it doesn't.
// The local singleplayer bus stays loopback regardless (attachToBus overrides
// this for the local URL).
//
// `webrtcViable` latches false after the first WebRTC failure, so a client on a
// network that can't do WebRTC (UDP blocked, etc.) pays the failover wait once
// rather than on every hop.
function makeSocketFactory(): BusSocketFactory {
  const webrtc: BusSocketFactory = (url) => new WebRtcBusSocket(url);
  const websocket: BusSocketFactory = (url) => new WebSocket(url);
  const state = { webrtcViable: true };
  return (url) => {
    if (!state.webrtcViable) return websocket(url);
    return new FallbackBusSocket(url, webrtc, websocket, () => { state.webrtcViable = false; });
  };
}

// Joiner transport (docs/p2p.md): when the page is opened from a share link
// (?join=<sessionId>), every bus connects P2P to the sharing host via the
// broker instead of the default server. WebRTC-only — the host is a browser, so
// there's no WebSocket to fall back to; a signaling failure just fails to
// connect. One shared signalOffer relays each socket's offer for the session.
function makeJoinSocketFactory(brokerUrl: string, session: string): BusSocketFactory {
  const signalOffer = brokerJoinSignal(brokerUrl, session);
  return (url) => new WebRtcBusSocket(url, { signalOffer });
}

// An explicit `?scripts=urlA,urlB,...` mod set (comma-separated ws/wss URLs),
// or null when the param is absent/empty. It stacks several mods as side-by-side
// tenants over one game — the array front door from docs/multi-script-client.md.
// (A dev-grade entry point: the product UX for assembling a set is still open —
// see the doc's "How the player assembles the set".) The load path falls back to
// the configured server (normal play) or the host's set (a joiner) when it's null.
function scriptsParam(): string[] | null {
  const scripts = new URLSearchParams(window.location.search).get("scripts");
  if (!scripts) return null;
  const urls = scripts.split(",").map(s => s.trim()).filter(s => /^wss?:\/\//.test(s));
  return urls.length > 0 ? urls : null;
}

async function romHashHex(rom: Uint8Array): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-1", rom as unknown as BufferSource);
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
}

async function readRomBytes(file: File): Promise<Uint8Array> {
  const buf = await file.arrayBuffer();
  return new Uint8Array(buf);
}

// A sink the host pushes one frame's audio samples to. Both AudioChannel
// (per tile) and any future test double satisfy it.
interface AudioSink {
  push(samples: Float32Array): void;
  // The sink's actual sample rate, so the core synthesizes at the rate the
  // worklet ring drains (no resampling) instead of a fixed 48 kHz.
  getSampleRate(): number;
}

// ----- battery-save persistence (docs/battery-save-persistence.md) -----
//
// The cartridge's battery-backed save file (Zelda-style saves), persisted
// locally and keyed by ROM + script bundle so a different mod set gets its own
// save. Host infrastructure, NOT a script's data — so it lives in a reserved KV
// scope a script can never address (a script's client scope is always
// `origin::label`, never this sentinel), walled off from the db_* surface.
const BATTERY_SCOPE = "__host_battery__";
const BATTERY_NS = "sram";
// One shared store for every tile. A generous quota because these are
// host-managed saves (many games/bundles) — not a script's 1 MiB budget.
const batteryStore = new LocalStorageMunosKv({ quotaPerNamespace: 64 * 1024 * 1024 });

// Build the host's battery-save port for one game session: read/write the
// reserved KV under a key that partitions saves by ROM, script bundle, and
// player slot (so couch players each keep their own file). The host supplies
// `bundleId` (a hash of its live tenant labels) per call and re-keys as the
// running bundle changes — a lobby that hops/spawns into a game switches the
// active save with it — so the key can't be baked in here.
function makeBatterySave(romHash: string, slot: number): BatterySaveHooks {
  const keyFor = (bundleId: string): string => `${romHash}::${bundleId}::${slot}`;
  return {
    load: (bundleId) => batteryStore.get(BATTERY_SCOPE, BATTERY_NS, keyFor(bundleId)).then(v => v ?? null),
    save: (bundleId, data) => { void batteryStore.set(BATTERY_SCOPE, BATTERY_NS, keyFor(bundleId), data); },
  };
}

// One emulator instance: the host (which is itself the networked Munos
// client). Input is NOT owned here — the central InputHub drives every
// tile's host by slot, so a single shared keyboard can be demuxed across
// tiles. The tile manager wires the InputHub handler after start().
class Session {
  private host: MunosScriptHost | null = null;

  getHost(): MunosScriptHost | null {
    return this.host;
  }

  async start(opts: {
    rom: Uint8Array;
    display: CanvasDisplay;
    audio: AudioSink;
    // The mod set to run over this game, in load order (>=1). Each becomes
    // a side-by-side tenant; index 0 donates the implicit orchestrator's
    // identity (docs/multi-script-client.md).
    mods: ModSpec[];
    romHash: string;
    coreId: string;
    // Transport factory for buses to real server URLs (WebRTC-first with a
    // WebSocket fallback). The local singleplayer bus ignores this and always
    // uses its loopback socket.
    socketFactory?: BusSocketFactory;
    // When true, this session is a P2P joiner: its initial mods ride one shared
    // JOIN bus to the sharing host (one peer), not a bus per URL (docs/p2p.md).
    joinBus?: boolean;
    // When true, this session is a LOCAL couch-joiner: its mods ride the shared
    // in-page LocalServer via the loopback (LOCAL_BUS_KEY), taking a fresh slot
    // on the primary's already-running hosted game. The in-page mirror of joinBus
    // — a [+] tile joining an in-browser P2P host, over loopback not WebRTC.
    localBus?: boolean;
    // The shared in-page local-server world (docs/p2p.md §split-screen). Every
    // tile gets the SAME hub so co-present couch players are in one hosted game;
    // one of them can Share it to remote friends who join the same server.
    localHub?: LocalHub;
    resolveDestination?: DestinationResolver;
    resolveDeployment?: DeploymentResolver;
    // Optional hook fired after each frame's pixels are pushed to the
    // canvas. The devtools panel uses this to refresh outlines /
    // matches grid / pose galleries without owning its own RAF loop.
    // Only the primary tile passes it (devtools attaches there).
    postFrame?: () => void;
    // This tile's stable player slot (0 = primary). Partitions the local
    // battery save so couch players each keep their own file.
    saveSlot: number;
    // The Rust/WASM compiler, booted once at page load. Threaded into the host
    // so tenant client halves compile through it; the shared LocalHub already
    // carries the same backend for the server halves.
    compiler?: MunosCompiler;
  }): Promise<void> {
    const { rom, display, audio,
            mods, romHash, coreId, socketFactory, joinBus, localBus, localHub, resolveDestination, resolveDeployment,
            postFrame, saveSlot, compiler } = opts;

    // Synthesize at the audio sink's ACTUAL rate (the AudioContext may have
    // refused 48 kHz and settled on the system rate). The worklet consumes
    // one ring frame per output sample with no resampling, so a core
    // running at a different rate would overrun the ring and play at the
    // wrong pitch.
    const sampleRate = audio.getSampleRate();
    const core = findCore(coreId).create({
      emulateSound: true,
      sampleRate,
    });
    // MunosScriptHost is system-agnostic for the play loop (GB and NES
    // both run), and devtools constructs the matching PpuView per system
    // at attach time. Size the canvas to the core's native resolution —
    // without this, a 160x144 GB framebuffer would render into the
    // bottom-left of a 256x240 canvas with garbage above/right. The
    // overscan inset crops the masked TV ring from the display (NES 8px
    // -> visible 240x224; GB 0).
    display.setSize(core.screenWidth, core.screenHeight, overscanForSystem(core.system));
    // Playtest knob: ?localTickHz=N throttles the in-page singleplayer server's
    // relay heartbeat (default 30 Hz), widening ghost-update gaps so the
    // renderer's coast + smoothing (SMOOTH_DEN) is observable. Omitted → default.
    const localTickHzParam = new URLSearchParams(window.location.search).get("localTickHz");
    const localTickHz = localTickHzParam !== null ? Number(localTickHzParam) : undefined;
    const hostOpts: MunosScriptHostOptions = {
      core,
      rom,
      // The mod set, in load order — each runs as a side-by-side tenant
      // over this one core. Each carries its own scriptLabel, which pairs
      // with its URL origin to scope client-side persistence
      // (docs/persistence.md), so stacked mods get distinct KV scopes.
      tenants: mods.map(m => ({
        source: m.source, url: m.url, scriptLabel: m.label,
        ...(joinBus ? { joinBus: true } : {}),
        ...(localBus ? { localBus: true } : {}),
      })),
      onFrame: (fb: Uint32Array) => { display.render(fb); postFrame?.(); },
      onAudio: (samples: Float32Array) => audio.push(samples),
      // Sound synthesis (docs/sound.md): play_sound builds a dedicated
      // synth core for the VGM's chip on demand and mixes it into onAudio.
      makeSynthCore: (chip: ChipId) => makeSynthCore(chip, sampleRate),
      audioSampleRate: sampleRate,
      // Only surface genuine failures; the info/connected status chatter is
      // runtime noise we don't want in the console.
      onStatus: (kind: "info" | "error" | "connected", msg: string) => {
        if (kind === "error") console.error(`[harness] ${msg}`);
      },
      romHash,
      ...(socketFactory !== undefined ? { socketFactory } : {}),
      ...(resolveDestination !== undefined ? { resolveDestination } : {}),
      ...(resolveDeployment !== undefined ? { resolveDeployment } : {}),
      ...(localTickHz !== undefined && Number.isFinite(localTickHz) && localTickHz > 0
        ? { localTickHz } : {}),
      // The shared local-server world, so this tile joins the couch's one game.
      ...(localHub !== undefined ? { localHub } : {}),
      // Persist the cartridge's battery save locally, keyed by ROM + the running
      // script bundle + player slot (docs/battery-save-persistence.md). The host
      // derives the bundle from its live tenants and re-keys as it hops/spawns;
      // it skips non-battery carts and a mod can suppress it via battery_persist.
      batterySave: makeBatterySave(romHash, saveSlot),
      ...(compiler !== undefined ? { compiler } : {}),
    };
    this.host = new MunosScriptHost(hostOpts);

    await this.host.start();
  }

  stop(): void {
    if (this.host) {
      this.host.stop();
      this.host = null;
    }
  }
}

// One on-screen emulator pane. `slot` is its stable player index (0 =
// primary). `row`/`col` are its logical position on the 2D grid (the
// origin tile is 0,0; growing in a direction places a neighbor at the
// adjacent cell — coordinates may go negative and are normalized to CSS
// grid lines at layout time). The primary tile carries
// id="screen"/.screen-wrap so the devtools panel keeps a fixed anchor.
interface Tile {
  slot: number;
  row: number;
  col: number;
  el: HTMLElement;
  canvas: HTMLCanvasElement;
  display: CanvasDisplay;
  audio: AudioChannel;
  session: Session;
}

// The directions a tile can grow in, with their (row, col) deltas. No
// north: the header tab strip lives in the space above each tile, so an
// upward [+] there crowds it — the grid grows down / left / right.
const GRID_DIRS = [
  { dir: "south", dr: 1, dc: 0 },
  { dir: "west", dr: 0, dc: -1 },
  { dir: "east", dr: 0, dc: 1 },
] as const;

// Everything the player calls into an open devtools panel. Exactly one
// of these exists while the panel is mounted — openDevtools installs
// it, closeDevtools clears it back to null — replacing the previous
// four independently reassignable function slots that had to be
// set/reset in lockstep. While the panel is closed every call site is
// a `?.` no-op, preserving the zero-interference path (one null check
// per emulator frame).
interface DevtoolsBridge {
  // Fired after each frame's pixels hit the canvas; refreshes
  // outlines / matches grid / pose galleries without the panel owning
  // its own RAF loop.
  onFrame(): void;
  // Tap for every NES button edge before it reaches the host, so TAS
  // recordings reflect what the user actually pressed (rather than
  // what the script consumed).
  onButton(btn: number, down: boolean): void;
  // Called after Session.start with the freshly-constructed host /
  // before session swaps, keeping transport / TAS / overlay state in
  // sync across ROM swaps.
  attachHost(host: MunosScriptHost): void;
  detachHost(): void;
}

// Mutable player-session state, grouped in one place so the launch /
// devtools paths share a single source of truth instead of ten loose
// closure variables.
interface PlayerState {
  // Session-wide fallback core id from resolveCoreId() (`?core=` URL
  // param, or DEFAULT_CORE_ID). Never mutated — each launch derives
  // its effective core id from the ROM filename and falls back here.
  readonly baseCoreId: string;
  // The currently-loaded ROM. Captured each launch so the devtools
  // panel can stamp identity (hash / name / size) onto adapter exports
  // and TAS recording metadata, and so a re-launch can reuse the same
  // bytes without round-tripping the ROM cache.
  activeRom: { hash: string; name: string; data: Uint8Array } | null;
  // The mod set the tiles are running (each: source + identity label + ws
  // URL), in load order — index 0 donates the implicit orchestrator's
  // identity. Cached so a newly-added tile boots the same set without
  // re-fetching mid-session.
  mods: ModSpec[] | null;
  // Non-null exactly while the devtools panel is mounted.
  devtools: DevtoolsBridge | null;
}

async function main(): Promise<void> {
  const overlay = document.getElementById("overlay");
  const loadButton = document.getElementById("load-button") as HTMLButtonElement | null;
  const romPicker = document.getElementById("rom-picker") as HTMLInputElement | null;
  const tilesContainer = document.getElementById("tiles");
  const controlsDialog = document.getElementById("controls-modal") as HTMLDialogElement | null;
  const controlsBody = document.getElementById("controls-modal-body");
  const controlsClose = document.getElementById("controls-modal-close") as HTMLButtonElement | null;
  if (!overlay || !loadButton || !romPicker || !tilesContainer ||
      !controlsDialog || !controlsBody || !controlsClose) {
    throw new Error("required DOM elements missing");
  }

  // Core is auto-detected from the ROM filename extension at launch
  // (.gb → GB, .nes → NES). baseCoreId only matters as a fallback for
  // unrecognized extensions; the `?core=` URL param still works as an
  // explicit override (e.g. for TAS replay against a hidden core).
  const state: PlayerState = {
    baseCoreId: resolveCoreId(),
    activeRom: null,
    mods: null,
    devtools: null,
  };

  // The picker's file filter comes from the core registry, not the
  // HTML — adding a core (or an extension to one) must not require a
  // markup edit.
  romPicker.accept = romPickerAccept();

  // Player page never scrolls — the grid is sized to fit. Hiding root
  // overflow lets the south-grow [+] overflow past the bottom edge and be
  // clipped instead of summoning a scrollbar.
  document.documentElement.classList.add("player-page");

  // No game yet: the grid-growing [+] buttons have nothing to grow around,
  // so hide them (CSS: body.pre-rom .tile-add) until the first ROM loads.
  document.body.classList.add("pre-rom");

  // Fullscreen "zen mode": when the page goes fullscreen — whether via F11
  // (browser chrome) or our own Fullscreen-API button later — hide every
  // interactive tile control (gear, volume, exit X, the [+] buttons) so
  // only the emulator screens and their player tags remain. A distraction-
  // free moment.
  //
  // F11 doesn't set document.fullscreenElement or fire fullscreenchange, so
  // we sniff it: display-mode:fullscreen, or the window covering the whole
  // screen (a few px of slop for rounding). The Fullscreen-API path is the
  // crisp document.fullscreenElement signal. Either kind flips zen on.
  // Floating fullscreen toggle — a subtle button hovering in the corner.
  // Drives the Fullscreen API on the whole page (so it matches F11); its
  // icon flips to an "exit" glyph while fullscreen, and syncZen keeps it in
  // step with F11 too. It stays clickable in zen mode (faded until hover)
  // so there's always a visible way back out.
  // Standard fullscreen icons: four corner brackets pointing outward to
  // enter, inward to exit (the conventional "maximize"/"minimize" glyphs).
  const FS_ENTER_ICON =
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M16 3h3a2 2 0 0 1 2 2v3"/><path d="M8 21H5a2 2 0 0 1-2-2v-3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/></svg>';
  const FS_EXIT_ICON =
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 3v3a2 2 0 0 1-2 2H3"/><path d="M21 8h-3a2 2 0 0 1-2-2V3"/><path d="M3 16h3a2 2 0 0 1 2 2v3"/><path d="M16 21v-3a2 2 0 0 1 2-2h3"/></svg>';

  const fsButton = document.createElement("button");
  fsButton.type = "button";
  fsButton.className = "fullscreen-toggle";
  fsButton.title = "Toggle fullscreen";
  fsButton.setAttribute("aria-label", "Toggle fullscreen");
  fsButton.addEventListener("click", () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen().catch(() => { /* denied */ });
  });
  document.body.append(fsButton);

  // Window/document listeners and timers that must not outlive the page.
  // pagehide drains this (below) so nothing keeps firing after the page is
  // gone (or, under bfcache, while it's frozen). Per-tile listeners are
  // discarded with their tile's DOM subtree and don't belong here.
  const teardowns: Array<() => void> = [];

  const fsMql = window.matchMedia("(display-mode: fullscreen)");
  const syncZen = (): void => {
    const apiFs = document.fullscreenElement !== null;
    const fillsScreen =
      Math.abs(window.innerHeight - screen.height) <= 2 &&
      Math.abs(window.innerWidth - screen.width) <= 2;
    const zen = apiFs || fsMql.matches || fillsScreen;
    document.documentElement.classList.toggle("zen", zen);
    // Exit-brackets while fullscreen, expand-brackets otherwise — reflects
    // whichever fullscreen kind is active.
    fsButton.innerHTML = zen ? FS_EXIT_ICON : FS_ENTER_ICON;
  };
  fsMql.addEventListener("change", syncZen);
  document.addEventListener("fullscreenchange", syncZen);
  // resize also affects zen (F11 doesn't fire fullscreenchange — syncZen
  // sniffs it via window-fills-screen), so the one resize handler below runs
  // syncZen too rather than registering a second independent resize listener.
  syncZen();
  teardowns.push(() => {
    fsMql.removeEventListener("change", syncZen);
    document.removeEventListener("fullscreenchange", syncZen);
  });

  const serverUrl = resolveServerUrl();
  const brokerUrl = resolveBrokerUrl();
  // Are we a JOINER? A share link (?join=<sessionId>) points the data plane at
  // the sharing host over WebRTC instead of the default server (docs/p2p.md).
  const joinSession = parseJoinSession();
  // A ?join= invite resolves to one of two modes, learned from the inviter's
  // session-info (session-info.ts) BEFORE any bus is created (boot awaits it):
  //   "p2p"    — the inviter's browser is the server → buses go P2P to it;
  //   "server" — the inviter is a client of a shared server → buses go to that
  //              server, exactly like normal play (the friend is routed fresh).
  // Both share ONE link format. joinMode is resolved by ensureJoinerSession; it
  // stays "server" until then, but no bus is built before the boot block sets it.
  let joinMode: "p2p" | "server" = "server";
  // Transport for buses to real server URLs. Normal play + a "server" join both
  // use WebRTC-first-with-WebSocket-fallback to the real server; a "p2p" join
  // routes every bus P2P to the host via the broker. Computed once and fanned to
  // every tile's session so they share one WebRTC-viability latch.
  const serverSocketFactory = makeSocketFactory();
  const p2pSocketFactory = joinSession !== null ? makeJoinSocketFactory(brokerUrl, joinSession) : null;
  const socketFactory: BusSocketFactory = joinSession === null
    ? serverSocketFactory
    : (url) => (joinMode === "p2p" ? p2pSocketFactory!(url) : serverSocketFactory(url));
  // One mixer for the page (the volume slider's gain + the worklet); each
  // tile draws a channel that sums into it ("mix all" couch audio).
  const mixer = new AudioMixer();
  // One input hub for the page; demuxes the shared keyboard + each
  // assigned gamepad to the owning tile by slot.
  const inputHub = new InputHub();
  inputHub.start();

  // One shared in-page local-server world for the whole couch (docs/p2p.md
  // §split-screen). Every tile joins it, so co-present players are clients of
  // ONE hosted game (each on its own slot) and any of them can Share it to
  // remote friends. Lazily backed — no server until a local mod launches — and
  // reset at each fresh-game boundary (doLoadRom / pagehide). A joiner (?join=)
  // never populates it (it's a thin client to a remote host), so it stays idle.
  const localTickHzParam = new URLSearchParams(window.location.search).get("localTickHz");
  const localTickHzRaw = localTickHzParam !== null ? Number(localTickHzParam) : undefined;
  // Boot the Rust/WASM compiler once for the whole page (async; then synchronous
  // per compile). It backs every tile's client halves (via the host) and the
  // shared LocalHub's singleplayer server halves — the production backend is the
  // WASM module, not the in-process TypeScript reference
  // (docs/rust-compiler-migration.md, Phase 6).
  const compiler = await loadBrowserCompiler();

  const localHub = new LocalHub({
    ...(localTickHzRaw !== undefined && Number.isFinite(localTickHzRaw) && localTickHzRaw > 0
      ? { tickHz: localTickHzRaw } : {}),
    compiler,
  });

  // The live tiles. At least one always exists. Exactly one is the
  // "primary": it carries id="screen"/.screen-wrap (the devtools anchor)
  // and hosts the "Load ROM" overlay. The primary is NOT fixed to a slot
  // — when it's removed, another tile is promoted (see promotePrimary).
  const tiles: Tile[] = [];
  let primarySlot = -1; // none yet; the first tile created claims it
  const tileBySlot = (slot: number): Tile | undefined => tiles.find(t => t.slot === slot);
  const tileAt = (row: number, col: number): Tile | undefined =>
    tiles.find(t => t.row === row && t.col === col);
  const primaryTile = (): Tile | undefined => tileBySlot(primarySlot);
  const primarySession = (): Session | null => primaryTile()?.session ?? null;
  const isPrimary = (tile: Tile): boolean => tile.slot === primarySlot;

  // Make `tile` the primary: give it the devtools anchor markers and move
  // the "Load ROM" overlay into it. Caller updates primarySlot.
  const applyPrimary = (tile: Tile): void => {
    primarySlot = tile.slot;
    tile.el.classList.add("screen-wrap");
    tile.canvas.id = "screen";
    tile.el.append(overlay);
    // Dock the Share tab in this tile's header. Hosting is a browser-global
    // action anchored to the primary (like the devtools/db tabs), so the one
    // button follows the primary across promotions/removals — moving the node,
    // not cloning it. It leads the control cluster, just left of the gear.
    const cog = tile.el.querySelector(".tile-tab-cog");
    if (cog) cog.before(shareButton);
  };

  // Tell the shared hub what a remote joiner's session-info bootstrap should learn
  // (session-info.ts), read LIVE at request time so it reflects the CURRENT game
  // after a lobby→game hop — never a load-time snapshot. The ROM hash lets the
  // joiner load the same game (and reject a wrong pick). Two shapes by how we're
  // playing:
  //   - P2P host (an in-page server half is live) → mode "p2p": the joiner runs
  //     our hosted mods and connects P2P to us;
  //   - plain client of a shared server → mode "server": the joiner connects to
  //     the SAME server at the game's stable deployment root (gameUrl) and is
  //     routed fresh — nothing is hosted through us.
  // The primary tile's CURRENT destination, read live — the one truth shared by
  // the remote-invite bootstrap and the local [+] couch-spawn (growFrom), so a
  // spawned tile lands in the exact game a friend's link would.
  const currentSessionInfo = (): HostSessionInfo => {
    const host = primarySession()?.getHost() ?? null;
    const romHash = state.activeRom?.hash ?? null;
    if (host?.canHost()) {
      return { mode: "p2p", modUrls: host.hostedModUrls(), romHash };
    }
    const gameUrl = host?.gameUrl() ?? null;
    return { mode: "server", modUrls: gameUrl !== null ? [gameUrl] : [], romHash };
  };
  localHub.setSessionInfoProvider(currentSessionInfo);

  // ----- P2P share (docs/p2p.md) -----
  // A "Share" tab in the primary tile's header (docked by applyPrimary). Shown
  // only when the primary tile is running a mod whose SERVER half is live in
  // this browser (host.canHost()) — otherwise it's absent, not a dead disabled
  // stub. Clicking it stands up P2P hosting and shows the copyable join link.
  // Never appears when this page is itself a joiner (?join=).
  const shareButton = document.createElement("button");
  shareButton.type = "button";
  shareButton.className = "tile-tab tile-tab-share";
  shareButton.textContent = "Invite";
  shareButton.title = "Invite a friend to your game (peer-to-peer)";
  shareButton.hidden = true;
  // Guards the async startHosting() so a double-click can't launch it twice.
  let startingHost = false;

  // The last-built share link, reused when reopening the modal while hosting.
  let lastShareUrl: string | null = null;

  // Reflect the primary host's shareability + invite state on the button. The tab
  // shows in three situations, none of them "disabled", and every one hands out a
  // ?join= link (the click handler picks P2P vs info-only hosting):
  //   - you're P2P-HOSTING an in-page game (canHost / isHosting);
  //   - you're a JOINER (?join=) — a static pass-along of the link you arrived on
  //     (handled below, set once at boot);
  //   - you're a plain client of a REMOTE server (the official server) with a
  //     game loaded — invite friends onto the SAME server to play alongside you.
  // Otherwise (no game up, nothing to invite to) the tab is absent.
  const refreshShareButton = (): void => {
    if (joinSession !== null) return; // joiner: static pass-along, set at boot
    const host = primarySession()?.getHost() ?? null;
    const hosting = host?.isHosting() ?? false;
    const canP2PHost = hosting || (host?.canHost() ?? false);
    shareButton.hidden = !canP2PHost && state.activeRom === null;
    shareButton.classList.toggle("sharing", hosting);
    shareButton.textContent = hosting ? "Inviting" : "Invite";
  };
  // Joiner: the pass-along link is valid from the start, so light the tab up now.
  if (joinSession !== null) shareButton.hidden = false;

  const shareModal = new ShareModal({
    peerCount: () => primarySession()?.getHost()?.peerCount() ?? 0,
  });

  shareButton.addEventListener("click", () => {
    // Joiner: hand the friend the very link we arrived on. A third player opening
    // it reaches the same inviter session — so it's a plain pass-along, whether
    // that session is P2P or server-mode. Nothing new is stood up here.
    if (joinSession !== null) {
      shareModal.open(buildJoinUrl(joinSession), "relay");
      return;
    }
    const host = primarySession()?.getHost() ?? null;
    if (host === null || startingHost) return;
    // Two ways to invite, both yielding ONE ?join= link (session-info tells the
    // joiner which):
    //   - P2P host (an in-page server half is live) → startHosting: friends
    //     become peers of our game; the modal shows a live peer count ("host").
    //   - plain client of a shared server → startInfoHosting: an info-only broker
    //     session so friends learn the game + ROM and join it ON THE SERVER; no
    //     peers of our own, so the relay modal (no count) fits.
    const p2pHost = host.canHost();
    const modalMode = p2pHost ? "host" : "relay";
    // Already inviting → just reopen the existing link.
    if (host.isHosting() && lastShareUrl !== null) {
      shareModal.open(lastShareUrl, modalMode);
      return;
    }
    startingHost = true;
    const started = p2pHost ? host.startHosting(brokerUrl) : host.startInfoHosting(brokerUrl);
    started.then(
      (session) => {
        lastShareUrl = buildJoinUrl(session);
        shareModal.open(lastShareUrl, modalMode);
      },
      (e: unknown) => {
        console.error("[invite] failed to start session:", e);
      },
    ).finally(() => {
      startingHost = false;
      refreshShareButton();
    });
  });

  // The local server gains its server-half members asynchronously (a mod spawns
  // them after launch), so poll to light the button up once hosting is possible.
  if (joinSession === null) {
    const shareButtonPoll = setInterval(refreshShareButton, 750);
    teardowns.push(() => clearInterval(shareButtonPoll));
  }

  // Strip the devtools-anchor markers off a tile that's ceasing to be
  // primary — the inverse of applyPrimary's class/id (the overlay node is
  // moved, not copied, so applyPrimary re-appending it onto the new primary
  // is enough; nothing to remove here for it). Used by promotePrimary for a
  // live switch between two existing tiles; removeTile doesn't need it (the
  // outgoing primary is already gone from the DOM).
  const demotePrimary = (tile: Tile): void => {
    tile.el.classList.remove("screen-wrap");
    tile.canvas.removeAttribute("id");
  };

  // Devtools re-anchor hook, wired by the devtools block below. The primary
  // tile IS the devtools anchor; when it changes (instance dropdown or a
  // primary removal), an OPEN panel re-anchors onto the given tile's
  // host/canvas/stage instead of closing — the user keeps their adapter /
  // TAS work. No-op when the panel is closed.
  let retargetDevtools: ((tile: Tile) => void) | null = null;

  // Devtools instance-list refresh hook. Called after any tile add / remove
  // / promotion so the panel's instance dropdown reflects the live tiles.
  // No-op when the panel is closed.
  let refreshDevtoolsInstances: (() => void) | null = null;

  // Devtools toggle hook, wired by the devtools block below. Mirrors the
  // Ctrl+` hotkey (open if closed, close if open) so the per-tile devtools
  // tab can drive the same panel. Stays null when there's no devtools-root,
  // which is the signal the per-tile tab uses to omit itself.
  let toggleDevtools: (() => void) | null = null;

  // Database admin panel toggle, wired by the db-admin block below. Like
  // toggleDevtools, it's the signal the per-tile DB tab uses to render
  // itself, and drives one shared panel from any tile.
  let toggleDatabasePanel: (() => void) | null = null;

  // Make the tile in `slot` the primary (devtools anchor). Demotes the
  // current primary, promotes the target, then re-anchors an open devtools
  // panel onto it. Driven by the panel's instance dropdown. No-op if the
  // slot is already primary or doesn't exist.
  const promotePrimary = (slot: number): void => {
    if (slot === primarySlot) return;
    const target = tileBySlot(slot);
    if (!target) return;
    const current = primaryTile();
    if (current) demotePrimary(current);
    applyPrimary(target);
    retargetDevtools?.(target);
    refreshDevtoolsInstances?.();
  };

  // Browsers gate audio behind a user gesture (autoplay policy). The
  // AudioContext stays suspended until we call resume() from inside a
  // gesture handler. Wire that up once at boot; calling resume() while
  // already running is a no-op, so the auto-resume path (no gesture
  // yet) just waits for the first input event from the user.
  const resumeAudioOnce = (): void => {
    void mixer.resume();
    window.removeEventListener("pointerdown", resumeAudioOnce);
    window.removeEventListener("keydown", resumeAudioOnce);
  };
  window.addEventListener("pointerdown", resumeAudioOnce);
  window.addEventListener("keydown", resumeAudioOnce);

  // The resolver always HTTP-fetches the destination URL on hop.
  // Scripts inline their own image assets as base64 inside the source,
  // so there's no side-channel state to ship alongside.
  const destinationResolver: DestinationResolver = async (url: string): Promise<HopDestination | null> => {
    try {
      const { source, label } = await fetchScriptSource(url);
      return { source, label };
    } catch (e) {
      console.error(`[harness] failed to fetch source for ${url}: ${(e as Error).message}`);
      return null;
    }
  };

  // Group-hop resolver: fetch the destination endpoint's FULL served array so
  // the host can reconcile the running scripts against it (a deployment → its
  // members; a bare script → a one-element array). ModSpec is structurally a
  // HopDestination (it adds `url`), so the array is returned as-is.
  const deploymentResolver: DeploymentResolver = async (url: string): Promise<HopDestination[] | null> => {
    try {
      return await fetchMods(url);
    } catch (e) {
      console.error(`[harness] failed to fetch deployment for ${url}: ${(e as Error).message}`);
      return null;
    }
  };

  // ----- tile lifecycle -----

  // For each tile whose south-grow [+] is clipped below the viewport, the
  // pointer-Y at/below which the [+] should peek back up. Empty entry =
  // not clipped (the [+] is fully on-screen, no peek needed).
  const southTriggerY = new Map<HTMLElement, number>();

  // Recompute which tiles' south [+] are clipped off the bottom of the
  // viewport (so the pointer-peek behavior only kicks in when it's
  // actually hidden). Called after every layout/scale change.
  const updateSouthClip = (): void => {
    for (const tile of tiles) {
      const btn = tile.el.querySelector<HTMLElement>(".tile-add.south");
      const shown = !!btn && btn.style.display !== "none";
      const r = tile.el.getBoundingClientRect();
      // How much of the [+] sits below the viewport, as a fraction of its
      // height. Peek only once it's mostly hidden (>= PEEK_FRACTION), so a
      // still-clickable button doesn't fly away from the cursor.
      const btnTop = r.bottom + SOUTH_MARGIN;
      const below = btnTop + SOUTH_HEIGHT - Math.max(btnTop, window.innerHeight);
      const fractionBelow = below > 0 ? below / SOUTH_HEIGHT : 0;
      const clipped = shown && fractionBelow >= SOUTH_PEEK_FRACTION;
      tile.el.classList.toggle("south-clipped", clipped);
      if (clipped) {
        southTriggerY.set(tile.el, r.bottom - SOUTH_PEEK_ZONE);
      } else {
        southTriggerY.delete(tile.el);
        tile.el.classList.remove("south-peek");
      }
    }
  };

  // Width the docked devtools panel steals from the play area, in CSS px.
  // The panel is a fixed-position right-edge sidebar; CSS shrinks the
  // .stage by this same width (see `body.with-devtools .stage` in main.css)
  // so the grid reflows into the space left of it. rescale() subtracts it
  // too so the chosen integer scale fits the narrower area rather than
  // letting the grid grow back under the panel. Returns 0 when devtools is
  // closed or floating (a floating window reserves no layout space — only
  // the docked `with-devtools` class is set when the sidebar is parked).
  const dockedDevtoolsWidth = (): number => {
    if (!document.body.classList.contains("with-devtools")) return 0;
    const root = document.getElementById("devtools-root");
    if (!root || root.hasAttribute("hidden")) return 0;
    return root.getBoundingClientRect().width;
  };

  // Pick one integer scale shared by every tile so the whole grid's
  // bounding box fits the viewport, then apply it to each tile. Scales
  // down (toward 1) when a big grid won't fit and up (toward
  // MAX_TILE_SCALE) to use the space when there's room. Recomputed on
  // add/remove (via relayout), on ROM load (native size may change), and
  // on window resize.
  const rescale = (): void => {
    if (tiles.length === 0) return;
    const rows = Math.max(...tiles.map(t => t.row)) - Math.min(...tiles.map(t => t.row)) + 1;
    const cols = Math.max(...tiles.map(t => t.col)) - Math.min(...tiles.map(t => t.col)) + 1;
    // Tiles run the same ROM, so dimensions are uniform. Use the visible
    // (post-overscan-crop) size — that's the on-screen footprint.
    const tileW = tiles[0]!.display.displayWidth;
    const tileH = tiles[0]!.display.displayHeight;
    const availW = document.documentElement.clientWidth - VIEWPORT_H_MARGIN - dockedDevtoolsWidth();
    const availH = window.innerHeight - VIEWPORT_V_MARGIN;
    const fitW = Math.floor((availW - (cols - 1) * GRID_COL_GAP) / (cols * tileW));
    const fitH = Math.floor((availH - (rows - 1) * GRID_ROW_GAP) / (rows * tileH));
    const scale = Math.max(1, Math.min(MAX_TILE_SCALE, fitW, fitH));
    for (const tile of tiles) tile.display.setScale(scale);
    // Tile geometry just changed — refresh which south [+] are clipped.
    updateSouthClip();
  };

  // The page's single resize handler. Recomputes zen/fullscreen state
  // (syncZen — cheap, and F11 only surfaces here) and the shared tile scale,
  // the latter coalesced to one pass per frame so a drag-resize doesn't
  // thrash layout. One listener, not two, so there's a single thing to tear
  // down and resize does one layout pass.
  let rescaleQueued = false;
  const onResize = (): void => {
    syncZen();
    if (rescaleQueued) return;
    rescaleQueued = true;
    requestAnimationFrame(() => { rescaleQueued = false; rescale(); });
  };
  window.addEventListener("resize", onResize);
  teardowns.push(() => window.removeEventListener("resize", onResize));

  // Re-place every tile on the CSS grid (logical r/c normalized so the
  // top-left tile lands on grid line 1) and refresh each tile's
  // directional [+] buttons: a button shows only when its neighbor cell
  // is empty, so the pluses live on the growing perimeter rather than
  // pointing into occupied cells. Finishes by rescaling, since the tile
  // count / grid shape just changed.
  const relayout = (): void => {
    if (tiles.length === 0) return;
    const minRow = Math.min(...tiles.map(t => t.row));
    const minCol = Math.min(...tiles.map(t => t.col));
    const maxCol = Math.max(...tiles.map(t => t.col));
    // Stereo placement: binary by grid column. Everything left of centre
    // goes hard-left, everything right goes hard-right, and the lone
    // middle column of an odd grid sits at a phantom centre. Math.sign of
    // the column's offset from centre gives exactly −1 / 0 / +1 (even
    // grids have no tile at centre, so they're purely L/R).
    const colCentre = (minCol + maxCol) / 2;
    // Can't exit the last tile — disable every X when only one remains.
    const lastOne = tiles.length <= 1;
    // A single window needs no "P1" label (CSS hides .tile-tag while the
    // container is .single-tile); the tag returns once a second tile joins.
    tilesContainer.classList.toggle("single-tile", tiles.length === 1);
    for (const tile of tiles) {
      tile.el.style.gridRowStart = String(tile.row - minRow + 1);
      tile.el.style.gridColumnStart = String(tile.col - minCol + 1);
      tile.audio.setPan(Math.sign(tile.col - colCentre));
      for (const { dir, dr, dc } of GRID_DIRS) {
        const btn = tile.el.querySelector<HTMLElement>(`.tile-add.${dir}`);
        if (!btn) continue;
        const blocked = tileAt(tile.row + dr, tile.col + dc) !== undefined;
        btn.style.display = blocked ? "none" : "";
      }
      const exit = tile.el.querySelector<HTMLButtonElement>(".tile-tab-exit");
      if (exit) exit.disabled = lastOne;
    }
    rescale();
  };

  // A [+] tile joining the primary's in-progress game: the resolved mod set to
  // run (the primary's current set, not the entry lobby) plus whether it rides
  // the in-page LocalServer loopback (P2P host) or the normal server factory.
  interface CouchJoin {
    mods: ModSpec[];
    localBus: boolean;
  }

  // (Re)launch a single tile against the active ROM + cached script. The
  // primary tile is the one devtools / TAS attach to.
  //
  // `couch` makes this tile a LOCAL couch-joiner of the primary's current game
  // (growFrom) instead of a cold boot of the entry mods: it runs the primary's
  // live set (post-hop) rather than state.mods, and — for a P2P host — rides the
  // in-page LocalServer over the loopback (localBus), taking a fresh slot. The
  // in-page twin of a friend joining your invite link.
  const launchTile = async (tile: Tile, couch?: CouchJoin): Promise<void> => {
    const rom = state.activeRom;
    const mods = couch ? couch.mods : state.mods;
    if (!rom || mods === null) return;
    const primary = isPrimary(tile);
    const coreId = inferCoreIdFromName(rom.name) ?? state.baseCoreId;

    // Detach devtools from the outgoing primary host, drop the old input
    // handler, and stop the old session before the new one comes up so
    // wasm + WebSocket free before re-acquiring.
    if (primary) state.devtools?.detachHost();
    inputHub.removeHandler(tile.slot);
    tile.session.stop();

    try {
      await tile.session.start({
        rom: rom.data,
        display: tile.display,
        audio: tile.audio,
        mods,
        romHash: rom.hash,
        coreId,
        socketFactory,
        // A P2P joiner's initial mods share one bus to the host (one peer). A
        // "server" join is a normal server client — a bus per URL, no join bus.
        // A couch tile is never a page-level joiner (host pages only).
        joinBus: !couch && joinSession !== null && joinMode === "p2p",
        // A P2P couch tile rides the in-page LocalServer's loopback, taking a
        // fresh slot on the primary's already-running hosted game.
        localBus: couch?.localBus ?? false,
        // Every tile shares the one couch hub, so local play is one game.
        localHub,
        // spawn_local uses the single-script resolver; group hops use the
        // array resolver.
        resolveDestination: destinationResolver,
        resolveDeployment: deploymentResolver,
        // Always wired, but only the current primary drives devtools —
        // checked at call time so a tile promoted to primary after launch
        // starts refreshing the panel without a relaunch.
        postFrame: () => { if (isPrimary(tile)) state.devtools?.onFrame(); },
        // This tile's player slot partitions its local battery save.
        saveSlot: tile.slot,
        // The page-wide WASM compiler backs this tile's client halves.
        compiler,
      });
    } catch (e) {
      console.error(`[harness] tile ${tile.slot} start failed:`, e);
      return;
    }

    const host = tile.session.getHost();
    if (!host) return;
    // Wire this tile's input. The primary tile also taps button edges to
    // the devtools TAS recorder before forwarding to the host.
    inputHub.setHandler(tile.slot, (btn, down) => {
      if (isPrimary(tile)) state.devtools?.onButton(btn, down);
      if (down) host.buttonDown(btn);
      else host.buttonUp(btn);
    });
    if (primary) state.devtools?.attachHost(host);
  };

  // Create a tile at grid cell (row, col) in the lowest free player slot.
  // Returns null if the cell is already occupied. Does not launch — the
  // caller launches if a ROM is loaded.
  const addTile = (row: number, col: number): Tile | null => {
    if (tileAt(row, col)) return null;
    const used = new Set(tiles.map(t => t.slot));
    let slot = 0;
    while (used.has(slot)) slot++;
    ensureProfiles(slot + 1);

    const el = document.createElement("div");
    el.className = "screen-tile";

    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 240;
    el.append(canvas);

    tilesContainer.append(el);

    const tile: Tile = {
      slot, row, col, el, canvas,
      display: new CanvasDisplay(canvas),
      audio: mixer.createChannel(tileVolume(slot)),
      session: new Session(),
    };

    // Per-instance tab strip above the screen ("title bar" without a bar).
    // Left-aligned: player tag + Controls. Right-aligned: volume (with a
    // click-to-mute note) + an X exit on the far right. Every tile has an
    // X; relayout() disables it on the last remaining tile, and removing
    // the primary promotes another tile to take its place.
    const header = document.createElement("div");
    header.className = "tile-header";

    // Player tag — a label, not a button (no border), so it reads as the
    // instance's name rather than an action.
    const tag = document.createElement("span");
    tag.className = "tile-tag";
    tag.textContent = `P${slot + 1}`;
    header.append(tag);

    const controlsTab = document.createElement("button");
    controlsTab.type = "button";
    controlsTab.className = "tile-tab tile-tab-cog";
    // Gear glyph (text-presentation variation selector keeps it monochrome
    // so it tints with the tab color instead of rendering as a color emoji).
    controlsTab.textContent = "⚙︎";
    controlsTab.title = `Controls for player ${slot + 1}`;
    controlsTab.setAttribute("aria-label", `Controls for player ${slot + 1}`);
    controlsTab.addEventListener("click", () => controlsModal.open(slot));
    header.append(controlsTab);

    // Devtools tab — opens the inspector panel, the click-driven twin of
    // the Ctrl+` hotkey (for setups where that chord isn't available). The
    // panel anchors to the primary tile, so every tile's tab drives the one
    // shared panel. Omitted entirely when there's no devtools-root to host
    // it (toggleDevtools stays null in that build).
    if (toggleDevtools) {
      const devtoolsTab = document.createElement("button");
      devtoolsTab.type = "button";
      devtoolsTab.className = "tile-tab tile-tab-devtools";
      devtoolsTab.textContent = "</>";
      devtoolsTab.title = "Toggle devtools (Ctrl+`)";
      devtoolsTab.setAttribute("aria-label", "Toggle devtools");
      devtoolsTab.addEventListener("click", () => toggleDevtools?.());
      header.append(devtoolsTab);
    }

    // Database tab — opens the SQL admin panel over the persistent server
    // store. Like the devtools tab, every tile's button drives the one shared
    // panel; omitted when there's no db-admin-root to host it.
    if (toggleDatabasePanel) {
      const dbTab = document.createElement("button");
      dbTab.type = "button";
      dbTab.className = "tile-tab tile-tab-database";
      dbTab.textContent = "DB";
      dbTab.title = "Database explorer";
      dbTab.setAttribute("aria-label", "Open database explorer");
      dbTab.addEventListener("click", () => toggleDatabasePanel?.());
      header.append(dbTab);
    }

    // Volume — a wider tab holding this tile's slider. Its ♪ note is a
    // stateless shortcut: it snaps the slider to 0 if there's any volume,
    // or back to 100% if already silent. No mute state of its own.
    const volTab = document.createElement("div");
    volTab.className = "tile-tab tile-tab-volume";
    const volSlider = document.createElement("input");
    volSlider.type = "range";
    volSlider.className = "tile-vol";
    volSlider.min = "0";
    volSlider.max = "100";
    volSlider.step = "1";
    volSlider.value = String(Math.round(tile.audio.getVolume() * 100));
    volSlider.setAttribute("aria-label", `Volume for player ${slot + 1}`);
    // Single path for every volume change (slider drag or ♪ snap): set the
    // gain, persist it, and keep the slider thumb in sync.
    const applyVolume = (pct: number): void => {
      const clamped = Math.max(0, Math.min(100, Math.round(pct)));
      volSlider.value = String(clamped);
      tile.audio.setVolume(clamped / 100);
      saveTileVolume(slot, clamped / 100);
    };
    volSlider.addEventListener("input", () => {
      const pct = Number(volSlider.value);
      if (!Number.isFinite(pct)) return;
      applyVolume(pct);
    });
    const muteToggle = document.createElement("button");
    muteToggle.type = "button";
    muteToggle.className = "vol-mute";
    muteToggle.textContent = "♪";
    muteToggle.title = `Toggle volume for player ${slot + 1}`;
    muteToggle.setAttribute("aria-label", muteToggle.title);
    muteToggle.addEventListener("click", () => {
      applyVolume(Number(volSlider.value) > 0 ? 0 : 100);
    });
    // Note sits to the right of the slider: louder (slider full) ends
    // nearer the ♪, quieter ends away from it — the intuitive direction.
    volTab.append(volSlider, muteToggle);
    header.append(volTab);

    const exitTab = document.createElement("button");
    exitTab.type = "button";
    exitTab.className = "tile-tab tile-tab-exit";
    exitTab.textContent = "✕";
    exitTab.title = `Remove player ${slot + 1}`;
    exitTab.setAttribute("aria-label", `Remove player ${slot + 1}`);
    exitTab.addEventListener("click", () => removeTile(slot));
    header.append(exitTab);
    el.append(header);

    // One [+] per cardinal direction. relayout() shows/hides each based
    // on neighbor occupancy; clicking grows a tile into that cell.
    for (const { dir } of GRID_DIRS) {
      const add = document.createElement("button");
      add.type = "button";
      add.className = `tile-add ${dir}`;
      add.title = `Add a player to the ${dir}`;
      add.setAttribute("aria-label", `Add a player to the ${dir}`);
      add.textContent = "+";
      add.addEventListener("click", () => void growFrom(tile, dir));
      el.append(add);
    }

    // South-grow peek: when this tile's south [+] is clipped off-screen,
    // moving the pointer into its bottom strip slides the [+] up over the
    // canvas. The trigger Y is cached by updateSouthClip (no layout reads
    // in the pointer hot path); absent = not clipped, so no peek.
    el.addEventListener("pointermove", (e) => {
      const triggerY = southTriggerY.get(el);
      if (triggerY === undefined) return;
      el.classList.toggle("south-peek", e.clientY >= triggerY);
    });
    el.addEventListener("pointerleave", () => el.classList.remove("south-peek"));

    tiles.push(tile);
    // The first tile to exist becomes primary (anchors devtools + hosts
    // the overlay). Later promotions happen in removeTile.
    if (primarySlot === -1) applyPrimary(tile);
    // A pad plugged in earlier with no free slot can now be claimed.
    inputHub.claimUnassignedPads();
    relayout();
    // A new instance is now selectable in the devtools dropdown.
    refreshDevtoolsInstances?.();
    return tile;
  };

  // What a [+]-spawned tile boots into so it joins the primary's game rather than
  // cold-starting the entry lobby: the primary's live destination, resolved the
  // SAME way a remote invite is (currentSessionInfo). Null → plain boot of the
  // entry mods (no game up yet, or a joiner page — where the existing joinBus
  // path already lands new tiles on the host).
  //   - mode "p2p"    → run the hosted client mods over the loopback (localBus),
  //                     a fresh slot on our in-page LocalServer.
  //   - mode "server" → run the game's front-door mods over the normal factory,
  //                     the same connect a server-mode joiner makes.
  const resolveCouchJoin = async (): Promise<CouchJoin | null> => {
    if (joinSession !== null) return null; // joiner page: existing joinBus path
    const info = currentSessionInfo();
    if (info.modUrls.length === 0) return null; // primary not in a game yet
    try {
      const mods = (await Promise.all(info.modUrls.map(fetchMods))).flat();
      return { localBus: info.mode === "p2p", mods };
    } catch (e) {
      // Couldn't fetch the current set — fall back to a plain entry boot.
      console.error("[couch] couldn't resolve the primary's game to join:", e);
      return null;
    }
  };

  // Grow a new tile out of an existing one in the given direction, then boot it
  // into the primary's current game (couch-join) or, failing that, the entry ROM.
  const growFrom = async (from: Tile, dir: (typeof GRID_DIRS)[number]["dir"]): Promise<void> => {
    const delta = GRID_DIRS.find(d => d.dir === dir)!;
    const tile = addTile(from.row + delta.dr, from.col + delta.dc);
    if (!tile || !state.activeRom) return;
    // Snapshot the ROM-load generation before the async couch-join fetch. If a
    // ROM (re)load starts while we resolve, its doLoadRom relaunches every tile —
    // including this freshly-added one — against the new game, so the mod set we
    // captured is stale and launching it here would double-boot the tile against
    // the wrong ROM. Defer to the reload: it owns this tile now.
    const gen = loadRomGen;
    const couch = await resolveCouchJoin();
    if (gen !== loadRomGen) return;
    void launchTile(tile, couch ?? undefined).catch((e: unknown) =>
      console.error("[harness] grow-tile launch failed:", e));
  };

  const removeTile = (slot: number): void => {
    if (tiles.length <= 1) return; // never remove the last tile
    const idx = tiles.findIndex(t => t.slot === slot);
    if (idx < 0) return;
    const tile = tiles[idx]!;
    const wasPrimary = isPrimary(tile);

    inputHub.removeHandler(slot);
    tile.session.stop();
    tile.audio.dispose();
    tile.el.remove();
    tiles.splice(idx, 1);

    if (wasPrimary) {
      // The removed tile held the devtools anchor + overlay. Promote the
      // lowest-slot survivor: applyPrimary moves the overlay and
      // id="screen"/.screen-wrap onto it. If devtools is open, re-anchor
      // the panel onto the survivor (it referenced the gone canvas/stage)
      // rather than closing — the user keeps their adapter / TAS work.
      const next = tiles.reduce((a, b) => (b.slot < a.slot ? b : a));
      applyPrimary(next);
      retargetDevtools?.(next);
    }

    // Keep the instance dropdown current — a removed non-primary tile still
    // changes the option set.
    refreshDevtoolsInstances?.();
    relayout();
    // The slot's player profile is intentionally kept so re-adding it
    // recovers the user's bindings.
  };

  // Load a ROM the user has already saved (picked from the home-built picker
  // modal). Returns null on success; returns an error message for the modal to
  // show inline when the ROM can't be used — a joiner picked one that isn't the
  // host's game, or its cached bytes are gone from IndexedDB.
  const chooseSavedRom = async (entry: RecentRom): Promise<string | null> => {
    const required = await requiredRomHash();
    if (required !== null && entry.hash !== required) return ROM_MISMATCH_MSG;
    const bytes = await getRom(entry.hash);
    if (!bytes) {
      console.warn(`[harness] cached bytes missing for ${entry.hash}; re-pick the ROM`);
      return `Couldn't find the saved data for "${entry.name}". Load it from your computer again.`;
    }
    rememberRom(entry);
    await loadRom(bytes, entry.name);
    return null;
  };

  // Serialize ROM loads. `loadRom` tears every tile down and brings it
  // back up (`launchTile` does `session.stop()` then `await
  // session.start()`); two overlapping loads would interleave those on
  // the same session — the second `stop()` lands while the first
  // `start()` is still pending, leaving an orphaned host with a live
  // WebSocket + WASM instance that nothing tears down. A monotonic
  // generation lets a superseded load drop out (latest pick wins), and
  // the in-flight chain guarantees no two loads run their teardown/
  // relaunch concurrently.
  // A JOINER learns from the HOST (session-info.ts) both which mods to run and
  // which ROM the session is playing — the short share link carries neither.
  // Fetched once over P2P and cached: reused for every reload and read for ROM
  // enforcement (a joiner may only load the host's game).
  let joinerSession: JoinerSessionInfo | null = null;
  const ensureJoinerSession = async (): Promise<JoinerSessionInfo> => {
    if (joinSession === null) throw new Error("ensureJoinerSession outside a join");
    if (joinerSession === null) {
      joinerSession = await requestSessionInfo(brokerUrl, joinSession);
      // Latch the transport mode before any bus is built (boot awaits this).
      joinMode = joinerSession.mode;
    }
    return joinerSession;
  };

  // The ROM hash a joiner is required to match — the game the inviter is playing,
  // learned from session-info (both "p2p" and "server" invites carry it). null
  // when not a joiner, the inviter reports no ROM, or the inviter is unreachable
  // (the load path then surfaces that on its own). Awaits the fetch so a pick made
  // before boot can't slip through. Both the picker and the file-pick path gate on
  // this, so a wrong cartridge never boots against the invited game.
  const requiredRomHash = async (): Promise<string | null> => {
    if (joinSession === null) return null;
    try { return (await ensureJoinerSession()).romHash; }
    catch { return null; }
  };
  const ROM_MISMATCH_MSG =
    "That's not the right game for this invite — load the same ROM your friend is playing.";

  // The mod URLs to load for a game, resolved per environment:
  //   - an explicit ?scripts= set always wins (dev / custom set);
  //   - a JOINER (?join=) with no ?scripts= runs whatever the host is running;
  //   - normal play falls back to the single configured server URL.
  const resolveLoadModUrls = async (): Promise<string[]> => {
    const explicit = scriptsParam();
    if (explicit) return explicit;
    if (joinSession !== null) return (await ensureJoinerSession()).modUrls;
    return [serverUrl];
  };

  let loadRomGen = 0;
  let loadRomInFlight: Promise<void> = Promise.resolve();

  const loadRom = (rom: Uint8Array, romName: string): Promise<void> => {
    const gen = ++loadRomGen;
    const run = loadRomInFlight.then(() => doLoadRom(rom, romName, gen));
    // Keep the chain alive across failures so later loads still proceed.
    loadRomInFlight = run.catch(() => {});
    return run;
  };

  // An invited joiner who already has the ROM could boot straight in, but a
  // gestureless auto-boot would construct the AudioContext outside a user
  // gesture — the browser's autoplay warning fires and the context can even
  // settle on the wrong sample rate. So gate the boot behind ONE click:
  // when the ROM is in our library, swap the overlay's "Load ROM" affordance
  // for a Start button and load on click (which also unblocks audio). A miss
  // (unknown hash, or bytes evicted) is a no-op — the Load ROM overlay stays
  // up and the enforced picker takes over. hash null → no-op.
  const startGateIfSaved = async (hash: string | null): Promise<void> => {
    if (hash === null) return;
    const saved = readRecentRoms().find(r => r.hash === hash);
    if (!saved) return;
    const bytes = await getRom(hash);
    if (!bytes) return;

    // The joiner already has the exact cartridge, so there's nothing to
    // pick — hide the picker button and offer a single Start.
    const label = saved.name.replace(/\.[^.]+$/, "");
    loadButton.hidden = true;
    const startButton = document.createElement("button");
    startButton.type = "button";
    startButton.className = "load-button";
    startButton.id = "start-button";
    startButton.textContent = `▶ Start ${label}`;
    overlay.appendChild(startButton);

    startButton.addEventListener("click", () => {
      if (startButton.disabled) return;
      startButton.disabled = true;
      startButton.textContent = `Starting ${label}…`;
      void loadRom(bytes, saved.name).then(() => {
        if (overlay.classList.contains("hidden")) {
          // Success (overlay hidden): the game is up. Drop the one-off
          // Start button and restore the normal Load ROM affordance so any
          // later overlay re-show (e.g. all tiles closed) behaves normally.
          startButton.remove();
          loadButton.hidden = false;
        } else {
          // doLoadRom re-showed the overlay — the load failed (e.g. mod
          // fetch). Re-arm the button so the joiner can retry.
          startButton.disabled = false;
          startButton.textContent = `▶ Start ${label}`;
        }
      });
    });
  };

  // Load a ROM into every tile — couch co-op runs the same game across
  // all of them. Fetches the mod set once and fans it out; each tile is
  // its own client (own WASM per mod, own canvas). Tenants sharing a ws URL
  // share ONE WebSocket — the shared bus (docs/multi-script-client.md) — so a
  // deployment's members ride one socket per tile, not one each.
  const doLoadRom = async (rom: Uint8Array, romName: string, gen: number): Promise<void> => {
    // Fetch the mod set and hash the ROM up front; a failure here is
    // global, so surface the overlay and bail before tearing down any
    // running tiles.
    let mods: ModSpec[];
    let romHash: string;
    try {
      // Each configured URL expands to its mod(s): a bare label → one script,
      // a deployment → its members (all on that one URL → one shared bus).
      // Flattened in order; index 0 still donates the orchestrator identity.
      const perUrl = await Promise.all((await resolveLoadModUrls()).map(fetchMods));
      mods = perUrl.flat();
      romHash = await romHashHex(rom);
    } catch (e) {
      console.error("[harness] load failed:", e);
      overlay.classList.remove("hidden");
      return;
    }

    // A newer load arrived while we were fetching/hashing — drop this
    // one before touching shared state or any tile session.
    if (gen !== loadRomGen) return;

    // ROM file extension picks the core per tile at launch (.gb → GB,
    // .nes → NES); see launchTile. The inferred id is deliberately NOT
    // written back into state.baseCoreId — that made inference sticky.
    state.activeRom = { hash: romHash, name: romName, data: rom };
    state.mods = mods;
    overlay.classList.add("hidden");
    // A game is up — reveal the grid-growing [+] buttons.
    document.body.classList.remove("pre-rom");

    // Fresh game across the whole couch: tear the shared in-page world down
    // once, up front, so stale server halves from the previous game don't
    // linger (the shared hub isn't torn down by any single tile's stop). The
    // tiles rebuild it below — the first to launch a local mod recreates the
    // server; the rest reuse it, joining as additional couch players.
    localHub.reset();

    // Re-launch every live tile against the new ROM. Sequential so the
    // outgoing hosts free their wasm/sockets before the next comes up.
    for (const tile of tiles) {
      await launchTile(tile);
    }
    // The core's native resolution may have changed (NES <-> GB), which
    // changes how many tiles fit — re-fit the grid to the viewport.
    rescale();
  };

  const openRomPicker = (): void => {
    romPicker.value = "";
    romPicker.click();
  };

  // The Load ROM button opens our home-built file picker (saved games in
  // a per-console folder tree), which in turn hands off to the OS file
  // dialog via openRomPicker when the user wants a brand-new ROM.
  const romPickerModal = new RomPickerModal({
    loadFromComputer: openRomPicker,
    chooseSaved: chooseSavedRom,
  });
  // With no saved games the picker would just be an empty shell over its
  // "Load from my computer" button, so skip straight to the OS file dialog;
  // open the modal only once there's a saved library worth browsing.
  loadButton.addEventListener("click", () => {
    if (readRecentRoms().length === 0) openRomPicker();
    else romPickerModal.open();
  });

  // Controls modal: shared instance, opened from each tile's Controls
  // tab. The modal pauses the shared InputHub while it's open so face
  // buttons and arrow keys can be captured for remapping without driving
  // any tile.
  const controlsModal = new ControlsModal(controlsDialog, controlsBody, {
    setInputEnabled: (enabled) => inputHub.setEnabled(enabled),
    getPlayerCount: () => tiles.length,
  });
  controlsClose.addEventListener("click", () => controlsDialog.close());

  romPicker.addEventListener("change", () => {
    void (async () => {
      const file = romPicker.files?.[0];
      if (!file) return;
      const rom = await readRomBytes(file);
      const hash = await romHashHex(rom);
      // A joiner may only load the host's game — reject a mismatch before it's
      // cached or launched, reopening the picker with the reason.
      const required = await requiredRomHash();
      if (required !== null && hash !== required) {
        romPickerModal.showError(ROM_MISMATCH_MSG);
        return;
      }
      await putRom(hash, rom);
      rememberRom({ hash, name: file.name });
      await loadRom(rom, file.name);
    })().catch((e: unknown) => console.error("[harness] ROM pick failed:", e));
  });

  window.addEventListener("pagehide", () => {
    // Release page-scoped timers + window/document listeners (share-button
    // poll, resize, fullscreen) so nothing keeps firing once the page is gone.
    for (const dispose of teardowns.splice(0)) dispose();
    for (const tile of tiles) tile.session.stop();
    // The tiles share one hub (no single stop() tears it down); close its
    // server + broker socket explicitly as the page goes away.
    localHub.reset();
    inputHub.stop();
  });

  // ----- devtools panel (Ctrl+Backquote toggle) -----
  //
  // The panel is lazy-loaded: until the user actually presses the
  // hotkey, no devtools code, CSS, or markup is in the page. This
  // preserves the zero-interference rule — visitors who never open
  // devtools pay zero bytes beyond the keydown listener and an empty
  // <aside id="devtools-root"> in the HTML.
  //
  // Hotkey: Ctrl + backtick (Ctrl+` / Ctrl+Backquote). We avoided F12
  // because modern browsers reserve it for their own devtools. Pairing
  // backtick with Ctrl frees the bare backtick to be a game-input key
  // (some retro setups bind it) and avoids any chance of the panel
  // toggling while the user is typing outside an <input>.
  const devtoolsRoot = document.getElementById("devtools-root");
  if (devtoolsRoot) {
    type DevtoolsModule = typeof import("./devtools/panel.js");
    type BuildDomModule = typeof import("./devtools/build-dom.js");

    let panel: InstanceType<DevtoolsModule["DevtoolsPanel"]> | null = null;
    let modulesPromise: Promise<[DevtoolsModule, BuildDomModule]> | null = null;

    const loadModules = (): Promise<[DevtoolsModule, BuildDomModule]> => {
      if (!modulesPromise) {
        modulesPromise = Promise.all([
          import("./devtools/panel.js"),
          import("./devtools/build-dom.js"),
        ]);
      }
      return modulesPromise;
    };

    const closeDevtools = (): void => {
      if (!panel) return;
      panel.unmount();
      panel = null;
      state.devtools = null;
      devtoolsRoot.setAttribute("hidden", "");
      devtoolsRoot.replaceChildren();
      document.body.classList.remove("with-devtools");
    };
    // Re-anchor an open panel onto `tile` when the primary changes (instance
    // dropdown switch or a primary removal). The panel swaps host + canvas +
    // stage live, preserving all in-panel state. No-op when closed.
    retargetDevtools = (tile: Tile): void => {
      panel?.retarget({
        host: tile.session.getHost(),
        canvas: tile.canvas,
        stage: tile.el,
      });
    };
    // Refresh the panel's instance dropdown after a tile add/remove/promotion.
    refreshDevtoolsInstances = (): void => { panel?.refreshInstances(); };

    const openDevtools = async (): Promise<void> => {
      // Devtools mounts against the current primary tile — its canvas and
      // wrapper are the id="screen"/.screen-wrap anchor. The primary IS the
      // inspected instance; the panel's instance dropdown promotes another
      // tile to primary (promotePrimary) to retarget, re-anchoring live.
      const primary = primaryTile();
      const screenWrap = document.querySelector<HTMLElement>(".screen-wrap");
      if (!primary || !screenWrap) {
        console.warn("[devtools] missing primary tile — cannot mount");
        return;
      }
      const canvas = primary.canvas;
      // Devtools constructs the right PpuView for the active core at
      // attach time (ppu-nes for NES sessions, ppu-gb for GB), so any
      // system the player supports is supported here. The per-tier
      // surface differences (e.g. GB's BG-mode tile picker doesn't have
      // a scanline slider) are handled inside the PpuView.
      const [{ DevtoolsPanel }, { buildPanelDom }] = await loadModules();
      const dom = buildPanelDom();
      devtoolsRoot.replaceChildren(dom.root);
      devtoolsRoot.removeAttribute("hidden");
      document.body.classList.add("with-devtools");

      // Game basename for the adapter export — strip .nes if present
      // so the export filename reads `mario.munos-adapter` not
      // `mario.nes.munos-adapter`. Empty until the user loads a ROM.
      const game = (state.activeRom?.name ?? "").replace(/\.nes$/i, "") || "rom";

      // The panel mounts with whatever host state the session has at
      // this moment — null pre-launch, a live host afterwards.
      // attachHost / detachHost on the panel keep it in sync across
      // ROM swaps; main.ts wires those into launch() below.
      const initialHost = primarySession()?.getHost() ?? null;
      panel = new DevtoolsPanel({
        ...(initialHost ? { host: initialHost } : {}),
        canvas,
        stage: screenWrap,
        // Read the CURRENT primary tile's canvas, not the one captured at
        // open: after a retarget the panel inspects a different tile, and its
        // canvas.width is the native res CanvasDisplay set for that session
        // (256 on NES, 160 on GB). Dividing displayed CSS width by it gives
        // the integer scale in use; falls back to 1 if width is somehow 0.
        getCanvasScale: () => {
          const c = primaryTile()?.canvas;
          return c ? Math.max(1, Math.round(c.clientWidth / (c.width || 1))) : 1;
        },
        game,
        romHash: state.activeRom?.hash ?? "",
        romBytes: state.activeRom?.data.length ?? 0,
        serverUrl,
        // Instance-target dropdown: the panel lists the live tiles and calls
        // back to switch which one it inspects. Reading tiles/primarySlot
        // live keeps the dropdown correct across add / remove / promotion.
        instances: () => [...tiles]
          .sort((a, b) => a.slot - b.slot)
          .map(t => ({ slot: t.slot, label: `P${t.slot + 1}` })),
        activeSlot: () => primarySlot,
        onSelectInstance: (slot: number) => promotePrimary(slot),
      });
      panel.mount(dom.root);
      // `panel` is non-null for as long as this bridge is installed:
      // closeDevtools nulls both together.
      state.devtools = {
        onFrame: () => panel!.onFrame(),
        onButton: (btn, down) => panel!.onButton(btn, down),
        attachHost: (h) => panel!.attachHost(h),
        detachHost: () => panel!.detachHost(),
      };
    };

    // Single open/close entry point shared by the Ctrl+` hotkey and the
    // per-tile devtools tab. Assigning it here (rather than null) is also
    // the signal addTile uses to render that tab at all.
    toggleDevtools = (): void => {
      if (panel) closeDevtools();
      else void openDevtools();
    };

    window.addEventListener("keydown", (e) => {
      if (e.code !== "Backquote") return;
      // Require Ctrl, but no other modifiers — Ctrl+Shift+` is the
      // user's terminal-toggle in VS Code and we don't want to fight
      // that, and Alt/Meta combos belong to the OS.
      if (!e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) return;
      // Don't fight a real text input — typing Ctrl+` into the
      // rename modal or controls modal shouldn't toggle devtools.
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;
      e.preventDefault();
      toggleDevtools?.();
    });
  }

  // ---- Database admin panel (lazy-loaded, like devtools) ----
  //
  // A click-driven SQL explorer over the persistent server store. The panel
  // module + its CSS are dynamic-imported on first open, so players who never
  // touch it pay nothing. getServerSqlDb() spins up (or reuses) the shared
  // OPFS worker, so the DB is inspectable even with no game running. A null
  // SQL handle (OPFS unavailable) still opens the panel — it shows a notice.
  const dbAdminRoot = document.getElementById("db-admin-root");
  if (dbAdminRoot) {
    type DbPanelModule = typeof import("./db-admin-panel.js");
    let dbPanel: InstanceType<DbPanelModule["DbAdminPanel"]> | null = null;

    const closeDb = (): void => {
      if (!dbPanel) return;
      dbPanel.unmount();
      dbPanel = null;
      dbAdminRoot.setAttribute("hidden", "");
      dbAdminRoot.replaceChildren();
    };

    const openDb = async (): Promise<void> => {
      const { DbAdminPanel } = await import("./db-admin-panel.js");
      // A second open while one is loading: bail if we already mounted.
      if (dbPanel) return;
      const sqlDb = await getServerSqlDb();
      dbAdminRoot.removeAttribute("hidden");
      dbPanel = new DbAdminPanel({
        query: sqlDb ? (sql, params) => sqlDb.query(sql, params) : null,
        rawValue: sqlDb ? (sql, params) => sqlDb.rawValue(sql, params) : null,
        storeLabel: `${SERVER_OPFS_DIR} · munos_kv`,
        onClose: () => closeDb(),
      });
      dbPanel.mount(dbAdminRoot);
    };

    toggleDatabasePanel = (): void => {
      if (dbPanel) closeDb();
      else void openDb();
    };
  }

  // Create the primary tile (slot 0) at grid origin. It always exists;
  // the overlay sits on top of it until a ROM loads. The directional [+]
  // buttons on each tile grow the grid from there.
  addTile(0, 0);

  // One-time, best-effort move of any legacy base64 ROM blobs out of
  // localStorage (where they were starving the Munos KV store's quota)
  // into IndexedDB. Awaited so a recent-ROM click can't race a ROM
  // that's mid-migration; with at most MAX_RECENT_ROMS entries this is
  // a few milliseconds, and a no-op after the first boot.
  await migrateLegacyRomCache();

  // An invited player is offered a one-click Start into the invited game when
  // we already have its ROM (the click also unblocks audio — see
  // startGateIfSaved). The session-info fetch (session-info.ts) tells us WHICH
  // ROM (and, via joinMode, whether the game is P2P-hosted or on a shared
  // server — already latched by ensureJoinerSession before any bus). If we
  // have the ROM, gate on Start; if not, the Load ROM overlay stays up and the
  // picker / file-pick paths only accept the matching ROM (requiredRomHash),
  // so an incompatible cartridge can't boot. Sharing ROM bytes would risk
  // copyright, so we always key off the hash.
  if (joinSession !== null) {
    try {
      const { romHash } = await ensureJoinerSession();
      await startGateIfSaved(romHash);
    } catch (e) {
      console.error("[join] couldn't reach the inviter for session info:", e);
      romPickerModal.showError(
        "Couldn't reach your friend to start the session — make sure they're still inviting, then reload.",
      );
    }
  }
}

main().catch(e => {
  console.error(e);
});
